'use client'

import { ReactNode, useEffect, useState } from 'react'
import { InfoBanner } from '@vinted/web-ui'

import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '@marketplace-web/browser/browser-storage-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'

import {
  Conversation,
  TransactionSide,
  EscrowBannerTrackingType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  userMessageThreadBannerClickLinkEvent,
  userMessageThreadBannerCloseEvent,
  userMessageThreadBannerViewEvent,
} from '@marketplace-web/messaging/tracking-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { ESCROW_HELP_URL } from '@marketplace-web/messaging/navigation-feature'

import { HIGH_DETRACTOR_SEGMENT_ID, LOW_DETRACTOR_SEGMENT_ID } from '../../constants/constants'

type Props = {
  conversation?: Conversation
}

enum EscrowBannerType {
  Warning = 'warning',
  Info = 'info',
}

const EscrowBanner = ({ conversation }: Props) => {
  const translate = useTranslate('conversation.escrow_banner')
  const { track } = useTracking()
  const userId = useSession().user?.id
  const winDeConversationBannerV2AbTest = useAbTest('win_de_conversation_banner_v2_1')
  const winDeConversationBannerV3AbTest = useAbTest('win_de_conversation_banner_v3')
  const trackAbTest = useTrackAbTestCallback()

  const [isBannerClosed, setIsBannerClosed] = useState(false)

  const conversationId = conversation?.id
  const userSide = conversation?.transaction?.userSide
  const isBuyer = userSide === TransactionSide.Buyer
  const isGermanyMarket = conversation?.germanyBannerMetadata
  const areUsersGermanyLowDetractors =
    conversation?.germanyBannerMetadata?.buyerSegment === LOW_DETRACTOR_SEGMENT_ID &&
    conversation.germanyBannerMetadata?.sellerSegment === LOW_DETRACTOR_SEGMENT_ID
  const isUserHighDetractor = isBuyer
    ? conversation?.germanyBannerMetadata?.buyerSegment === HIGH_DETRACTOR_SEGMENT_ID
    : conversation?.germanyBannerMetadata?.sellerSegment === HIGH_DETRACTOR_SEGMENT_ID

  const shouldHideBanner =
    winDeConversationBannerV2AbTest?.variant === 'on' && areUsersGermanyLowDetractors
  const showHighDetractorBanner =
    winDeConversationBannerV3AbTest?.variant === 'on' && isUserHighDetractor

  const IS_GERMANY_ESCROW_BANNER_SEEN_KEY = `is_germany_escrow_banner_seen-${String(conversationId)}`
  const BANNER_CATEGORY = 'non_escrow_banner'

  const trackingEventData = {
    transactionId: String(conversation?.transactionId),
    userId: String(userId),
    userSide: userSide || null,
    bannerCategory: BANNER_CATEGORY,
    bannerType: EscrowBannerTrackingType.Warning,
  }

  useEffect(() => {
    if (
      shouldHideBanner ||
      !isGermanyMarket ||
      isBannerClosed ||
      getSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY) === 'true'
    )
      return

    track(userMessageThreadBannerViewEvent(trackingEventData))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHideBanner, isGermanyMarket, isBannerClosed, track])

  useEffect(() => {
    if (areUsersGermanyLowDetractors) trackAbTest(winDeConversationBannerV2AbTest)
  }, [areUsersGermanyLowDetractors, trackAbTest, winDeConversationBannerV2AbTest])

  useEffect(() => {
    if (isUserHighDetractor) trackAbTest(winDeConversationBannerV3AbTest)
  }, [isUserHighDetractor, trackAbTest, winDeConversationBannerV3AbTest])

  const closeBanner = () => {
    setIsBannerClosed(true)
    setSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY, 'true')

    track(userMessageThreadBannerCloseEvent(trackingEventData))
  }

  const trackLinkClick = () => {
    track(userMessageThreadBannerClickLinkEvent(trackingEventData))
  }

  const getBannerTitle = () => {
    if (showHighDetractorBanner) return translate(`${userSide}.high_detractor.title`)

    return translate(`${userSide}.${EscrowBannerType.Warning}.title`)
  }

  const getBannerBody = () => {
    const values = {
      'faq-link': (chunks: Array<ReactNode>) => (
        <a
          key={ESCROW_HELP_URL}
          href={ESCROW_HELP_URL}
          target="_blank"
          rel="noreferrer"
          onClick={trackLinkClick}
        >
          {chunks}
        </a>
      ),
    }

    if (showHighDetractorBanner) return translate(`${userSide}.high_detractor.body`, values)

    return translate(`${userSide}.${EscrowBannerType.Warning}.body`, values)
  }

  const renderEscrowBanner = () => {
    if (shouldHideBanner || !isGermanyMarket || !userSide || isBannerClosed) return null

    return (
      <InfoBanner
        title={getBannerTitle()}
        body={getBannerBody()}
        closable
        type="warning"
        onClose={closeBanner}
      />
    )
  }

  if (getSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY) === 'true') return null

  return renderEscrowBanner()
}

export default EscrowBanner
